import Entity from "_framework/entities/entity";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import Guid from "_shared/guids/guid";
import AdministratorInvitationSchema, {
  AdministratorInvitationJSON,
  AdministratorInvitationParams,
} from "legal-entities/invitations/administrator-invitations/_schema/administrator-invitation-schema";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import LegalEntity from "legal-entities/legal-entity";
import RegistrationInfo from "legal-entities/registration-info/registration-info";

export default class AdministratorInvitation extends Entity<
  AdministratorInvitation,
  AdministratorInvitationJSON,
  AdministratorInvitationParams
> {
  public static create(values: AdministratorInvitationParams): AdministratorInvitation {
    const parsed = AdministratorInvitationSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new InvalidAdministratorInvitationError("Invalid AdministratorInvitation parameters.");
    }
    return new AdministratorInvitation(parsed);
  }

  public static fromJSON(json: unknown): AdministratorInvitation | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const params = AdministratorInvitationSchema.deserialize(json);
    return new AdministratorInvitation(params);
  }

  public static fromJSONArray(jsonArray: unknown): AdministratorInvitation[] | null | undefined {
    if (isNullOrUndefined(jsonArray)) return jsonArray;
    const invitations: AdministratorInvitation[] = [];
    for (const json of jsonArray as AdministratorInvitationJSON[]) {
      const params = AdministratorInvitationSchema.deserialize(json);
      invitations.push(new AdministratorInvitation(params));
    }
    return invitations;
  }

  public readonly id: Guid;
  public readonly entity: LegalEntity;
  // TODO: Missing "userId: Guid" field?
  public readonly registrationInfo: RegistrationInfo; // TODO: Doesn't exist on the server-side AdministratorInvitation class.
  public readonly status: InvitationStatus;
  public readonly message?: string; // TODO: Doesn't exist on the server-side AdministratorInvitation class.

  public constructor(params: AdministratorInvitationParams) {
    const validatedParams = AdministratorInvitationSchema.validate(params);
    super(validatedParams);
    this.id = validatedParams.id;
    this.entity = LegalEntity.create(validatedParams.entity);
    this.registrationInfo = RegistrationInfo.create({ params: validatedParams.registrationInfo });
    this.status = validatedParams.status ?? InvitationStatus.Open;
    this.message = validatedParams.message;
  }

  public toJSON(): AdministratorInvitationJSON {
    return AdministratorInvitationSchema.serialize(this);
  }

  public valueOf(): AdministratorInvitationJSON {
    return this.toJSON();
  }

  public isEqualTo(other: AdministratorInvitation | null | undefined): boolean {
    return this.id.isEqualTo(other?.id);
  }

  public toString(): string {
    return `AdministratorInvitation (
      id: ${this.id.value}
      entity: ${this.entity.toString()}
      registrationInfo: ${this.registrationInfo.toString()}
      status: ${this.status}
    )`;
  }
}

export class AdministratorInvitationNotFoundError extends Error {
  public constructor(id: Guid) {
    super(`Unable to find invitation by id: ${id}`);
  }
}

export class UnauthorizedAdministratorInvitationAccessError extends Error {
  public constructor(entityId: Guid) {
    super(`You are not permitted to view all administrator invitations for this entity: ${entityId}.`);
  }
}

export class InvalidAdministratorInvitationError extends Error {}
